import { ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppState } from "../../contexts/App";
import { type ImageCropResult } from "../ImageCropModal";
import { CropProps, UploadImageInput } from "../UploadImageInput";
import {
  Action,
  Actions,
  type ActionTone,
  Frame,
  type ImageFieldShape,
  Row,
  Spinner,
  Texts,
  Thumb,
  ThumbButton,
} from "./styles";

export { Action as ImageFieldAction, type ActionTone, Frame as ImageFieldFrame, type ImageFieldShape };

/** The 4:3 image of the sections (Sobre, the catalog's items, Mídia): 1200 x 900. */
export const LANDSCAPE_CROP: CropProps = {
  slice: { aspect: 4 / 3 },
  size: { width: 1200, height: 900 },
  cropShape: "rect",
};

const ImageIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="m21 15-5-5L5 21" />
  </svg>
);

type RowProps = {
  shape: ImageFieldShape;
  /** What goes in the thumbnail (an image, an emoji); nothing draws the empty, dashed one. */
  thumb?: ReactNode;
  /** The drawing of the empty thumbnail: an image's by default. */
  emptyIcon?: ReactNode;
  /** The thumbnail is a shortcut to the main action: wraps it (e.g. the file input's label). */
  wrapThumb?: (thumb: ReactNode) => ReactNode;
  uploading?: boolean;
  error?: boolean;
  title?: string;
  required?: boolean;
  hint?: string;
  /** Above the thumbnail, in the same frame (e.g. the choice between an image and an emoji). */
  header?: ReactNode;
  /** The buttons of the strip below (ImageFieldAction); none hides the strip. */
  actions?: ReactNode;
  /** Inside another card: takes the editor's background. */
  inset?: boolean;
};

/**
 * The layout of every image of the editor, in one frame: the thumbnail with its name and hint, the actions
 * in a strip below. ImageField uses it for images; it's also there for what takes an image's place (an emoji).
 */
export const ImageFieldRow = ({
  shape,
  thumb,
  emptyIcon,
  wrapThumb,
  uploading,
  error,
  title,
  required,
  hint,
  header,
  actions,
  inset,
}: RowProps) => {
  const thumbBox = (
    <Thumb $shape={shape} $empty={!thumb} $error={error}>
      {thumb ?? emptyIcon ?? <ImageIcon />}
      {uploading && (
        <span className="veil">
          <Spinner />
        </span>
      )}
    </Thumb>
  );

  return (
    <Frame $inset={inset}>
      {header}
      <Row>
        {wrapThumb ? <ThumbButton>{wrapThumb(thumbBox)}</ThumbButton> : thumbBox}
        <Texts>
          {title && (
            <span className="title">
              {title}
              {required && " *"}
            </span>
          )}
          {hint && (
            <span className={error ? "hint error" : "hint"} role={error ? "alert" : undefined}>
              {hint}
            </span>
          )}
        </Texts>
      </Row>
      {actions && <Actions>{actions}</Actions>}
    </Frame>
  );
};

type Props = {
  image?: string;
  onChange: (image?: string, cropResult?: ImageCropResult) => void;
  shape: ImageFieldShape;
  crop?: CropProps;
  accept?: string;
  title?: string;
  required?: boolean;
  hint?: string;
  /** Shown while there's no image of the owner's (e.g. the share image the API made); can't be removed. */
  placeholderImage?: string;
  /** The drawing of the empty thumbnail: an image's by default. */
  emptyIcon?: ReactNode;
  /** The buttons' texts, when the screen names its image (a photo): "Adicionar imagem" / "Trocar imagem". */
  addLabel?: string;
  changeLabel?: string;
  header?: ReactNode;
  inset?: boolean;
};

/**
 * Sending an image of the editor: its thumbnail, with "Trocar imagem" and "Remover" in the strip below
 * ("Adicionar imagem" while there's none). The upload shows in place, the screen stays usable, and a failed
 * upload says so. Removing doesn't ask: nothing is saved before the screen's "Salvar".
 */
export const ImageField = ({
  image,
  onChange,
  shape,
  crop,
  accept = "image/*",
  title,
  required,
  hint,
  placeholderImage,
  emptyIcon,
  addLabel,
  changeLabel,
  header,
  inset,
}: Props) => {
  const { t } = useTranslation();
  const siteId = useAppState().appState?.editingSiteId as string;
  const inputId = useId();
  const [isUploading, setUploading] = useState(false);
  const [hasError, setError] = useState(false);

  const shown = image || placeholderImage;
  const openPicker = () => {
    if (isUploading) {
      return;
    }
    setError(false);
    document.getElementById(inputId)?.click();
  };

  const mainLabel = isUploading
    ? t("components.imageField.uploading")
    : hasError
      ? t("components.imageField.retry")
      : image
        ? changeLabel || t("components.imageField.change")
        : addLabel || t("components.imageField.add");

  return (
    <ImageFieldRow
      shape={shape}
      thumb={shown ? <img src={shown} alt={title || ""} /> : undefined}
      emptyIcon={emptyIcon}
      wrapThumb={thumb => (
        <UploadImageInput
          accept={accept}
          siteId={siteId}
          crop={crop}
          inputId={inputId}
          onUploadStart={() => {
            setError(false);
            setUploading(true);
          }}
          onUploadError={() => {
            setUploading(false);
            setError(true);
          }}
          onUpload={(uploaded, cropResult) => {
            setUploading(false);
            onChange(uploaded, cropResult);
          }}>
          {thumb}
        </UploadImageInput>
      )}
      uploading={isUploading}
      error={hasError}
      title={title}
      required={required}
      hint={hasError ? t("components.imageField.error") : hint}
      header={header}
      inset={inset}
      actions={
        <>
          <Action
            type="button"
            $tone={!image && !hasError ? "primary" : "default"}
            // aria-disabled, not disabled: the button keeps the keyboard's focus while the file goes up
            aria-disabled={isUploading}
            onClick={openPicker}>
            {isUploading && <Spinner $size={14} />}
            {mainLabel}
          </Action>
          {image && !isUploading && (
            <Action type="button" $tone="danger" onClick={() => onChange(undefined)}>
              {t("components.imageField.remove")}
            </Action>
          )}
        </>
      }
    />
  );
};
